import React, { useState } from 'react';
import { User, Globe } from 'lucide-react';
import { useApp } from '../../lib/AppContext';
import { LANGUAGES } from '../../lib/vocabulary';
import { Language } from '../../types';

const AVATAR_OPTIONS = ['👦', '👧', '🧒', '👶', '🐶', '🐱', '🐼', '🦁', '🐯', '🦊', '🐰', '🐻'];

export function ProfileSetup() {
  const { setUserProfile } = useApp();
  const [name, setName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(AVATAR_OPTIONS[0]);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('en');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    setUserProfile({
      name: name.trim(),
      avatar: selectedAvatar,
      preferredLanguage: selectedLanguage,
      currentLevel: 1,
      createdAt: Date.now(),
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 p-4 flex items-center justify-center">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full animate-fadeIn">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🎮</div>
          <h1 className="text-4xl font-bold text-primary mb-2">Welcome to Word Adventure!</h1>
          <p className="text-gray-600">Let's set up your profile to get started</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Input */}
          <div>
            <label className="block text-lg font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <User size={20} />
              What's your name?
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              maxLength={20}
              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          {/* Avatar Selection */}
          <div>
            <label className="block text-lg font-semibold text-gray-700 mb-3">
              Choose your avatar
            </label>
            <div className="grid grid-cols-6 gap-3">
              {AVATAR_OPTIONS.map((avatar) => (
                <button
                  key={avatar}
                  type="button"
                  onClick={() => setSelectedAvatar(avatar)}
                  className={`
                    text-4xl p-4 rounded-xl transition-all duration-200
                    ${
                      selectedAvatar === avatar
                        ? 'bg-blue-500 scale-110 shadow-lg'
                        : 'bg-gray-100 hover:bg-gray-200 hover:scale-105'
                    }
                  `}
                >
                  {avatar}
                </button>
              ))}
            </div>
          </div>

          {/* Language Selection */}
          <div>
            <label className="block text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Globe size={20} />
              What language do you want to learn?
            </label>
            <div className="grid grid-cols-2 gap-3">
              {LANGUAGES.slice(0, 8).map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setSelectedLanguage(lang.code)}
                  className={`
                    p-4 rounded-xl text-left transition-all duration-200 flex items-center gap-3
                    ${
                      selectedLanguage === lang.code
                        ? 'bg-blue-500 text-white shadow-lg scale-105'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }
                  `}
                >
                  <span className="text-3xl">{lang.flag}</span>
                  <span className="font-semibold">{lang.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!name.trim()}
            className="btn-primary w-full text-xl py-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Start Learning! 🚀
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Your progress will be saved automatically</p>
        </div>
      </div>
    </div>
  );
}
